import React, { useState, useRef } from 'react';
import './StandardInspectionForm.css';
import Notification from '../Notification';
import ActionConfirmModal from '../ActionConfirmModal/ActionConfirmModal';
import inspectionService from '../../services/inspectionService';
import photoService from '../../services/photoService';
import { useAuthContext } from '../../context/AuthContext';
import { ROLES, INSURANCE_COMPANIES, VEHICLE_TYPES, OWNER_DOCUMENT_TYPES } from '../../utils/constants';
import { useSpinner } from '../../context/SpinnerContext';
import logger from '../../utils/logger';
import { PROPERTY_CARD_HINTS } from '../../constants/propertyCardHints';
import FieldMismatchNotice from '../FieldMismatchNotice/FieldMismatchNotice';
import { PROPERTY_CARD_FORM_FIELDS, valuesMismatch } from '../../utils/propertyCardCrossCheck';
import config from '../../config/environment';

const { ADMINISTRATOR } = ROLES;

const StandardInspectionForm = ({ onSubmit, onCancel, onClose, onInspectionCreated }) => {
    const { currentUser } = useAuthContext();
    const { showSpinner, hideSpinner } = useSpinner();
    const [notification, setNotification] = useState({ visible: false, message: '', type: 'info' });
    const licensePlateInputRef = useRef(null);

    const showNotification = (message, type = 'info') => {
        setNotification({ visible: true, message, type });
        setTimeout(() => {
            setNotification({ visible: false, message: '', type: 'info' });
        }, 5000);
    };

    // Estados para los datos del formulario
    const [vehicleData, setVehicleData] = useState({
        licensePlate: '',
        plateType: '',
        brand: '',
        line: '',
        year: '',
        displacement: '',
        color: '',
        type: '',
        fuel: '',
        capacity: '',
        engine: '',
        chassis: '',
        mobilityRestriction: '',
        armor: '',
        importDeclaration: '',
        importDate: '',
        propertyLimitation: '',
        transitAgency: '',
        fasecoCode: '',
        fasecoValue: ''
    });

    const [contactData, setContactData] = useState({
        name: '',
        phone: '',
        email: '',
        docType: '',
        docNumber: ''
    });

    const [intermediaryData, setIntermediaryData] = useState({
        intermediary: '',
        insuranceCompany: ''
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [confirmedLicensePlate, setConfirmedLicensePlate] = useState('');
    const [showPlateConfirmModal, setShowPlateConfirmModal] = useState(false);
    const [currentStep, setCurrentStep] = useState(config.ENABLE_PROPERTY_CARD_OCR ? 1 : 2);
    const [propertyCardSessionId, setPropertyCardSessionId] = useState('');
    const [propertyCardFiles, setPropertyCardFiles] = useState({
        propertyCardFront: null,
        propertyCardBack: null,
    });
    const [extractedPropertyCard, setExtractedPropertyCard] = useState(null);
    const [skippedPropertyCardRead, setSkippedPropertyCardRead] = useState(false);
    const [propertyCardReadError, setPropertyCardReadError] = useState('');
    const [acceptedMismatches, setAcceptedMismatches] = useState({});
    const [visibleMismatches, setVisibleMismatches] = useState({});

    // Opciones para los dropdowns
    const plateTypes = [
        { value: 'PARTICULAR', label: 'Particular' },
        { value: 'PUBLICA', label: 'Pública' },
        { value: 'DIPLOMATICA', label: 'Diplomática' },
        { value: 'OFICIAL', label: 'Oficial' },
        { value: 'OTRA', label: 'Otra' }
    ];

    const fuelTypes = [
        { value: 'GASOLINA', label: 'Gasolina' },
        { value: 'DIESEL', label: 'Diésel' },
        { value: 'ELECTRICO', label: 'Eléctrico' },
        { value: 'HIBRIDO', label: 'Híbrido' }
    ];

    const yesNoOptions = [
        { value: true, label: 'Sí' },
        { value: false, label: 'No' }
    ];

    // Función para manejar cambios en datos del vehículo
    const handleVehicleDataChange = (field, value) => {
        setVehicleData(prev => ({
            ...prev,
            [field]: value
        }));
        if (field === 'licensePlate' && value !== confirmedLicensePlate) {
            setConfirmedLicensePlate('');
        }
        if (acceptedMismatches[field]) {
            setAcceptedMismatches(prev => ({ ...prev, [field]: false }));
        }
    };

    const handleLicensePlateBlur = () => {
        const plate = vehicleData.licensePlate?.trim();
        if (!plate) return;
        if (plate === confirmedLicensePlate) return;
        setShowPlateConfirmModal(true);
    };

    const handleConfirmPlate = () => {
        setConfirmedLicensePlate(vehicleData.licensePlate.trim());
        setShowPlateConfirmModal(false);
    };

    const handleReviewPlate = () => {
        setShowPlateConfirmModal(false);
        setTimeout(() => {
            licensePlateInputRef.current?.focus();
            licensePlateInputRef.current?.select?.();
        }, 0);
    };

    const isPlateConfirmed = () => {
        const plate = vehicleData.licensePlate?.trim();
        return !plate || plate === confirmedLicensePlate;
    };

    // Función para manejar cambios en datos de contacto
    const handleContactDataChange = (field, value) => {
        setContactData(prev => ({
            ...prev,
            [field]: value
        }));
        if (acceptedMismatches[field]) {
            setAcceptedMismatches(prev => ({ ...prev, [field]: false }));
        }
    };

    // Función para manejar cambios en datos de intermediario
    const handleIntermediaryDataChange = (field, value) => {
        setIntermediaryData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const getExtractedValue = (formKey) => {
        const mapping = PROPERTY_CARD_FORM_FIELDS.find(item => item.formKey === formKey);
        if (!mapping || !extractedPropertyCard) return '';
        return extractedPropertyCard[mapping.extractedKey] || '';
    };

    const canCrossCheckField = (formKey) => {
        if (!extractedPropertyCard || skippedPropertyCardRead || acceptedMismatches[formKey]) return false;
        return Boolean(getExtractedValue(formKey));
    };

    const handleExtractedFieldBlur = (formKey, currentValue) => {
        if (!canCrossCheckField(formKey)) {
            setVisibleMismatches(prev => ({ ...prev, [formKey]: false }));
            return;
        }
        const extractedValue = getExtractedValue(formKey);
        setVisibleMismatches(prev => ({
            ...prev,
            [formKey]: valuesMismatch(currentValue, extractedValue, formKey),
        }));
    };

    const mapExtractedFuel = (value) => {
        const raw = String(value || '').toUpperCase();
        if (!raw) return '';
        if (fuelTypes.some((type) => type.value === raw)) return raw;
        if (raw.includes('DIESEL') || raw.includes('DIÉSEL')) return 'DIESEL';
        if (raw.includes('ELECTRIC')) return 'ELECTRICO';
        if (raw.includes('HIBRID')) return 'HIBRIDO';
        if (raw.includes('GASOLINA')) return 'GASOLINA';
        return '';
    };

    const handlePropertyCardFile = (photoId, file) => {
        if (!file || !file.type.startsWith('image/')) return;
        const reader = new FileReader();
        reader.onload = (e) => {
            setPropertyCardFiles(prev => ({
                ...prev,
                [photoId]: {
                    file,
                    preview: e.target.result,
                    name: file.name,
                },
            }));
            setSkippedPropertyCardRead(false);
            setExtractedPropertyCard(null);
            setPropertyCardReadError('');
        };
        reader.readAsDataURL(file);
    };

    const applyExtractedPropertyCard = (propertyCard) => {
        setExtractedPropertyCard(propertyCard);
        setSkippedPropertyCardRead(false);
        setAcceptedMismatches({});
        setVisibleMismatches({});
        setVehicleData(prev => ({
            ...prev,
            licensePlate: propertyCard.licensePlate || prev.licensePlate,
            brand: propertyCard.brand || prev.brand,
            line: propertyCard.line || prev.line,
            year: propertyCard.year || prev.year,
            fuel: mapExtractedFuel(propertyCard.fuel) || prev.fuel,
            engine: propertyCard.engine || prev.engine,
            chassis: propertyCard.chassis || propertyCard.vin || prev.chassis,
        }));
        setContactData(prev => ({
            ...prev,
            name: propertyCard.ownerName || prev.name,
            docType: propertyCard.docType || prev.docType,
            docNumber: propertyCard.docNumber || prev.docNumber,
        }));
        if (propertyCard.licensePlate) {
            setConfirmedLicensePlate(propertyCard.licensePlate);
        }
    };

    const uploadPropertyCardPhotos = async () => {
        if (propertyCardSessionId) return propertyCardSessionId;
        const { sessionId, uploadUrls } = await inspectionService.generatePropertyCardUploadUrls();
        const frontUrl = uploadUrls.find(item => item.id === 'propertyCardFront');
        const backUrl = uploadUrls.find(item => item.id === 'propertyCardBack');
        await photoService.uploadPhotoToS3(frontUrl, propertyCardFiles.propertyCardFront.file);
        await photoService.uploadPhotoToS3(backUrl, propertyCardFiles.propertyCardBack.file);
        setPropertyCardSessionId(sessionId);
        return sessionId;
    };

    const handleReadPropertyCard = async () => {
        if (!propertyCardFiles.propertyCardFront?.file || !propertyCardFiles.propertyCardBack?.file) {
            showNotification('Carga el frente y el reverso de la tarjeta de propiedad.', 'error');
            return;
        }

        showSpinner('Leyendo el documento...');
        try {
            const sessionId = await uploadPropertyCardPhotos();
            const result = await inspectionService.readPropertyCard(sessionId);
            applyExtractedPropertyCard(result.propertyCard || {});
            setPropertyCardReadError('');
            hideSpinner();
            setCurrentStep(2);
            if (result.missingFields?.length) {
                showNotification(
                    'Se leyeron algunos datos de la tarjeta. Completa los campos que faltan para continuar.',
                    'warning'
                );
            }
        } catch (error) {
            hideSpinner();
            logger.error('Error leyendo la tarjeta de propiedad:', error, null, 'StandardInspectionForm');
            setPropertyCardReadError(error.message || 'No se pudo leer el documento.');
        }
    };

    const handleSkipPropertyCardRead = async () => {
        const hasBothPhotos = Boolean(
            propertyCardFiles.propertyCardFront?.file && propertyCardFiles.propertyCardBack?.file
        );

        if (hasBothPhotos) {
            showSpinner('Guardando fotos del documento...');
            try {
                await uploadPropertyCardPhotos();
            } catch (error) {
                logger.error('Error guardando fotos de la tarjeta:', error, null, 'StandardInspectionForm');
                showNotification('No se pudieron guardar las fotos del documento. Continúa e ingresa los datos manualmente.', 'warning');
            } finally {
                hideSpinner();
            }
        }

        setSkippedPropertyCardRead(true);
        setExtractedPropertyCard(null);
        setPropertyCardReadError('');
        setAcceptedMismatches({});
        setVisibleMismatches({});
        setCurrentStep(2);
    };

    const renderMismatchNotice = (formKey) => {
        if (!canCrossCheckField(formKey) || !visibleMismatches[formKey]) return null;
        return (
            <FieldMismatchNotice
                extractedValue={getExtractedValue(formKey)}
                onAcceptTyped={() => {
                    setAcceptedMismatches(prev => ({ ...prev, [formKey]: true }));
                    setVisibleMismatches(prev => ({ ...prev, [formKey]: false }));
                }}
                onDismiss={() => setVisibleMismatches(prev => ({ ...prev, [formKey]: false }))}
            />
        );
    };

    const renderPropertyCardSlot = (photoId) => {
        const hint = PROPERTY_CARD_HINTS[photoId];
        const selected = propertyCardFiles[photoId];
        return (
            <div className="property-card-slot" key={photoId}>
                <div className="property-card-slot-body">
                    <h3>{hint.title}</h3>
                    <p className="property-card-subtitle">{hint.subtitle}</p>
                    <div className="property-card-image-frame">
                        <img
                            src={selected?.preview || hint.image}
                            alt={selected?.name || hint.title}
                        />
                    </div>
                    <p className="property-card-hint-text">{hint.hint}</p>
                </div>
                <div className="property-card-slot-footer">
                    {selected?.preview ? (
                        <button
                            type="button"
                            className="property-card-upload property-card-remove"
                            onClick={() => {
                                setPropertyCardFiles(prev => ({ ...prev, [photoId]: null }));
                                setExtractedPropertyCard(null);
                                setSkippedPropertyCardRead(false);
                            }}
                        >
                            Quitar
                        </button>
                    ) : (
                        <label className="property-card-upload">
                            <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => handlePropertyCardFile(photoId, e.target.files?.[0])}
                            />
                            Cargar foto
                        </label>
                    )}
                </div>
            </div>
        );
    };

    const isFieldEmpty = (value) => {
        return !value || value.trim() === '';
    };

    // Función para obtener la clase CSS del campo
    const getFieldClassName = (value, disabled = false) => {
        return `form-input ${isFieldEmpty(value) && !disabled ? 'error' : ''}`;
    };

    // Función para validar si el formulario está completo
    const isFormComplete = () => {
        let requiredVehicleFields = [
            'licensePlate',
            'plateType',
            'brand',
            'line',
            'year',
            'type',
            'fuel',
            'chassis',
            'fasecoCode',
        ];

        const extraVehicleFields = ['armor'];

        if (vehicleData.type === 'LIVIANO') {
            requiredVehicleFields = requiredVehicleFields.concat(extraVehicleFields);
        }

        const requiredContactFields = config.ENABLE_OFFICIAL_RECORDS
            ? ['name', 'phone', 'email']
            : ['phone', 'email'];
        const requiredIntermediaryFields = ['intermediary', 'insuranceCompany'];
        return (
            requiredVehicleFields.every(field => !isFieldEmpty(vehicleData[field])) &&
            requiredContactFields.every(field => !isFieldEmpty(contactData[field])) &&
            requiredIntermediaryFields.every(field => !isFieldEmpty(intermediaryData[field])) &&
            isPlateConfirmed()
        ); 
    };

    // Función para crear la inspección
    const createInspection = async () => {
        if (!isFormComplete()) {
            if (!isPlateConfirmed() && vehicleData.licensePlate?.trim()) {
                setShowPlateConfirmModal(true);
                return;
            }
            showNotification('Por favor complete todos los campos requeridos', 'error');
            return;
        }

        showSpinner('Creando inspección...');
        logger.info('Creando inspección...', {
            currentUserEmail: currentUser?.email,
        }, 'StandardInspectionForm');
        setIsSubmitting(true);

        try {
            const inspectionData = {
                userId: String(currentUser.id),
                userName: String(currentUser.name),
                userEmail: String(currentUser.email),
                inspectionId: String('INS'),
                licensePlate: String(vehicleData.licensePlate),
                plateType: String(vehicleData.plateType),
                brand: String(vehicleData.brand),
                line: String(vehicleData.line),
                year: String(vehicleData.year),
                type: String(vehicleData.type),
                fuel: String(vehicleData.fuel),
                chassis: String(vehicleData.chassis),
                engine: String(vehicleData.engine || ''),
                armor: String(vehicleData.armor),
                fasecoCode: String(vehicleData.fasecoCode),
                name: String(contactData.name),
                phone: String(contactData.phone),
                email: String(contactData.email),
                docType: String(contactData.docType),
                docNumber: String(contactData.docNumber),
                intermediary: String(intermediaryData.intermediary),
                insuranceCompany: String(intermediaryData.insuranceCompany),
                propertyCardSessionId: propertyCardSessionId || null
            };

            const result = await inspectionService.createInspection(inspectionData);

            hideSpinner();
            setIsSubmitting(false);

            showNotification('Inspección creada exitosamente', 'success');
            logger.info('Inspección creada exitosamente...', {
                inspectionId: result.inspectionId,
                currentUserEmail: currentUser?.email,
            }, 'StandardInspectionForm');

            if (onInspectionCreated && typeof onInspectionCreated === 'function') {
                onInspectionCreated(result.inspection || result);
            }

            if (typeof onClose === 'function') {
                onClose();
            }

        } catch (error) {
            logger.error('Error creating inspection:', error, null, 'StandardInspectionForm');
            hideSpinner();
            setIsSubmitting(false);
            showNotification('Error al crear la inspección. Inténtalo de nuevo.', 'error');
        }
    };

    const fillInspectionData = () => {
        setVehicleData({
            licensePlate: 'ABC123',
            plateType: 'PARTICULAR',
            brand: 'Renault',
            line: 'Logan',
            year: '2020',
            fuel: 'GASOLINA',
            chassis: 'CHS1234567890',
            type: 'LIVIANO',
            armor: 'NO',
            fasecoCode: 'FSC001',
        });
        setConfirmedLicensePlate('ABC123');
        setContactData({
            name: 'Juan Pérez',
            phone: '5551234567',
            email: 'juan.perez@example.com',
            docType: 'CC',
            docNumber: '1010111935'
        });
        setIntermediaryData({
            intermediary: 'Intermediario',
            insuranceCompany: 'HOLDING'
        });
        setSkippedPropertyCardRead(true);
        setCurrentStep(2);
    };

    const enableFillInspectionData = () => {
        return currentUser.role === ADMINISTRATOR && window.location.origin.includes('localhost');
    };

    return (
        <div className="standard-inspection-form">
            <div className="form-container">
                {config.ENABLE_PROPERTY_CARD_OCR && (
                <div className="progress-bar">
                    <div className="progress-step">
                        <div className={`step-number ${currentStep >= 1 ? 'active' : ''}`}>1</div>
                        <span className="step-label">Tarjeta de propiedad</span>
                    </div>
                    <div className="progress-step">
                        <div className={`step-number ${currentStep >= 2 ? 'active' : ''}`}>2</div>
                        <span className="step-label">Datos de la Inspección</span>
                    </div>
                </div>
                )}

                <div className="form-content">
                    {currentStep === 1 ? (
                        <>
                            <h2>Tarjeta de propiedad</h2>
                            <p className="form-description">
                                Carga el frente y el reverso de la licencia de tránsito para leer los datos del vehículo y del propietario.
                                Si vas a ingresar todo a mano, puedes omitir la lectura y continuar.
                            </p>
                            <div className="property-card-slots">
                                {renderPropertyCardSlot('propertyCardFront')}
                                {renderPropertyCardSlot('propertyCardBack')}
                            </div>
                            {propertyCardReadError && (
                                <div className="property-card-read-error" role="alert">
                                    <p>{propertyCardReadError}</p>
                                    <div className="property-card-read-error-actions">
                                        <button
                                            type="button"
                                            className="btn-standard btn-standard-secondary"
                                            onClick={() => {
                                                setPropertyCardFiles({ propertyCardFront: null, propertyCardBack: null });
                                                setPropertyCardReadError('');
                                            }}
                                        >
                                            Volver a cargar
                                        </button>
                                        <button
                                            type="button"
                                            className="btn-standard btn-standard-primary"
                                            onClick={handleSkipPropertyCardRead}
                                        >
                                            Omitir OCR y continuar
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                    <h2>Inspección Digital de Vehículo</h2>
                    <p className="form-description">
                        Complete todos los datos requeridos para crear la inspección digital del vehículo.
                    </p>

                    <div className="form-sections">
                        {/* Datos del Vehículo */}
                        <div className="form-section">
                            <h3>Datos del Vehículo</h3>

                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="licensePlate">Número de Placa</label>
                                    <input
                                        type="text"
                                        id="licensePlate"
                                        ref={licensePlateInputRef}
                                        className={getFieldClassName(vehicleData.licensePlate)}
                                        value={vehicleData.licensePlate}
                                        onChange={(e) => handleVehicleDataChange('licensePlate', e.target.value.toUpperCase())}
                                        onBlur={(e) => {
                                            handleLicensePlateBlur();
                                            handleExtractedFieldBlur('licensePlate', e.target.value);
                                        }}
                                        placeholder="ABC123"
                                        required
                                    />
                                    {renderMismatchNotice('licensePlate')}
                                </div>
                                <div className="form-group">
                                    <label htmlFor="plateType">Tipo de Placa</label>
                                    <select
                                        id="plateType"
                                        value={vehicleData.plateType}
                                        className={getFieldClassName(vehicleData.plateType)}
                                        onChange={(e) => handleVehicleDataChange('plateType', e.target.value)}
                                        required
                                    >
                                        <option value="">Seleccione</option>
                                        {plateTypes.map(type => (
                                            <option key={type.value} value={type.value}>
                                                {type.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="brand">Marca</label>
                                    <input
                                        type="text"
                                        id="brand"
                                        className={getFieldClassName(vehicleData.brand)}
                                        value={vehicleData.brand}
                                        onChange={(e) => handleVehicleDataChange('brand', e.target.value)}
                                        onBlur={(e) => handleExtractedFieldBlur('brand', e.target.value)}
                                        placeholder="Renault"
                                        required
                                    />
                                    {renderMismatchNotice('brand')}
                                </div>
                                <div className="form-group">
                                    <label htmlFor="line">Línea</label>
                                    <input
                                        type="text"
                                        id="line"
                                        className={getFieldClassName(vehicleData.line)}
                                        value={vehicleData.line}
                                        onChange={(e) => handleVehicleDataChange('line', e.target.value)}
                                        onBlur={(e) => handleExtractedFieldBlur('line', e.target.value)}
                                        placeholder="Logan"
                                        required
                                    />
                                    {renderMismatchNotice('line')}
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="year">Año Modelo</label>
                                    <input
                                        type="number"
                                        id="year"
                                        className={getFieldClassName(vehicleData.year)}
                                        value={vehicleData.year}
                                        onChange={(e) => handleVehicleDataChange('year', e.target.value)}
                                        onBlur={(e) => handleExtractedFieldBlur('year', e.target.value)}
                                        placeholder="2020"
                                        min="1990"
                                        max={new Date().getFullYear() + 1}
                                        required
                                    />
                                    {renderMismatchNotice('year')}
                                </div>
                                <div className="form-group">
                                    <label htmlFor="type">Tipo de Vehículo</label>
                                    <select
                                        id="type"
                                        value={vehicleData.type}
                                        className={getFieldClassName(vehicleData.type)}
                                        onChange={(e) => handleVehicleDataChange('type', e.target.value)}
                                        required
                                    >
                                        <option value="" selected>Selecciona una opción</option>
                                        {VEHICLE_TYPES.map(type => (
                                            <option key={type.value} value={type.value}>
                                                {type.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="fuel">Combustible</label>
                                    <select
                                        id="fuel"
                                        value={vehicleData.fuel}
                                        className={getFieldClassName(vehicleData.fuel)}
                                        onChange={(e) => handleVehicleDataChange('fuel', e.target.value)}
                                        onBlur={(e) => handleExtractedFieldBlur('fuel', e.target.value)}
                                        required
                                    >
                                        <option value="">Seleccione</option>
                                        {fuelTypes.map(type => (
                                            <option key={type.value} value={type.value}>
                                                {type.label}
                                            </option>
                                        ))}
                                    </select>
                                    {renderMismatchNotice('fuel')}
                                </div>

                                <div className="form-group">
                                    <label htmlFor="chassis">Chasis o Vin</label>
                                    <input
                                        type="text"
                                        id="chassis"
                                        className={getFieldClassName(vehicleData.chassis)}
                                        value={vehicleData.chassis}
                                        onChange={(e) => handleVehicleDataChange('chassis', e.target.value)}
                                        onBlur={(e) => handleExtractedFieldBlur('chassis', e.target.value)}
                                        placeholder="CHS1234567890"
                                        required
                                    />
                                    {renderMismatchNotice('chassis')}
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="armor">Blindaje</label>
                                    <select
                                        id="armor"
                                        value={vehicleData.armor}
                                        className={getFieldClassName(vehicleData.armor, vehicleData.type === 'MOTOCICLETA')}
                                        onChange={(e) => handleVehicleDataChange('armor', e.target.value)}
                                        required={false}
                                        disabled={vehicleData.type === 'MOTOCICLETA'}
                                    >
                                        <option value="">Seleccione</option>
                                        {yesNoOptions.map(option => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="fasecoCode">Código fasecolda</label>
                                    <input
                                        type="text"
                                        id="fasecoCode"
                                        className={getFieldClassName(vehicleData.fasecoCode)}
                                        value={vehicleData.fasecoCode}
                                        onChange={(e) => handleVehicleDataChange('fasecoCode', e.target.value)}
                                        placeholder="FSC001"
                                        required
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Datos de Contacto y Aliado */}
                        <div className="form-section">
                            <h3>Datos de Contacto</h3>
                            {config.ENABLE_OFFICIAL_RECORDS && (
                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="name">Nombre del Propietario</label>
                                    <input
                                        type="text"
                                        id="name"
                                        className={getFieldClassName(contactData.name)}
                                        value={contactData.name}
                                        onChange={(e) => handleContactDataChange('name', e.target.value)}
                                        onBlur={(e) => handleExtractedFieldBlur('name', e.target.value)}
                                        placeholder="Juan Pérez"
                                        required
                                    />
                                    {renderMismatchNotice('name')}
                                </div>
                                <div className="form-group">
                                    <label htmlFor="docType">Tipo de documento</label>
                                    <select
                                        id="docType"
                                        className="form-input"
                                        value={contactData.docType}
                                        onChange={(e) => handleContactDataChange('docType', e.target.value)}
                                        onBlur={(e) => handleExtractedFieldBlur('docType', e.target.value)}
                                    >
                                        <option value="">Seleccione</option>
                                        {OWNER_DOCUMENT_TYPES.map(type => (
                                            <option key={type.value} value={type.value}>
                                                {type.label}
                                            </option>
                                        ))}
                                    </select>
                                    {renderMismatchNotice('docType')}
                                </div>
                            </div>
                            )}
                            <div className="form-row">
                                {config.ENABLE_OFFICIAL_RECORDS && (
                                <div className="form-group">
                                    <label htmlFor="docNumber">Número de documento</label>
                                    <input
                                        type="text"
                                        id="docNumber"
                                        className="form-input"
                                        value={contactData.docNumber}
                                        onChange={(e) => handleContactDataChange('docNumber', e.target.value)}
                                        onBlur={(e) => handleExtractedFieldBlur('docNumber', e.target.value)}
                                        placeholder="1234567890"
                                    />
                                    {renderMismatchNotice('docNumber')}
                                </div>
                                )}
                                <div className="form-group">
                                    <label htmlFor="phone">Celular de Contacto</label>
                                    <input
                                        type="tel"
                                        id="phone"
                                        className={getFieldClassName(contactData.phone)}
                                        value={contactData.phone}
                                        onChange={(e) => handleContactDataChange('phone', e.target.value)}
                                        placeholder="3001234567"
                                        required
                                    />
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="email">Correo Electrónico</label>
                                    <input
                                        type="email"
                                        id="email"
                                        className={getFieldClassName(contactData.email)}
                                        value={contactData.email}
                                        onChange={(e) => handleContactDataChange('email', e.target.value)}
                                        placeholder="juan.perez@email.com"
                                        required
                                    />
                                </div>
                            </div>

                            <h3 style={{ marginTop: '30px' }}>Datos del Aliado</h3>
                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="intermediary">Intermediario</label>
                                    <input
                                        type="text"
                                        id="intermediary"
                                        value={intermediaryData.intermediary}
                                        className={getFieldClassName(intermediaryData.intermediary)}
                                        onChange={(e) => handleIntermediaryDataChange('intermediary', e.target.value)}
                                        placeholder="Código Intermediario"
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="insuranceCompany">Nombre de la Compañía</label>
                                    <select
                                        id="insuranceCompany"
                                        value={intermediaryData.insuranceCompany}
                                        className={getFieldClassName(intermediaryData.insuranceCompany)}
                                        onChange={(e) => handleIntermediaryDataChange('insuranceCompany', e.target.value)}
                                        required
                                    >
                                        <option value="">Selecciona una compañía</option>
                                        {INSURANCE_COMPANIES.filter(company => company.label !== 'TODAS').map(company => (
                                            <option key={company.value} value={company.value}>
                                                {company.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>
                        </>
                    )}
                </div>

                {/* Navigation Buttons */}
                <div className="form-navigation">
                    {currentStep === 2 && config.ENABLE_PROPERTY_CARD_OCR && (
                    <button
                        type="button"
                        className="btn-standard btn-standard-secondary"
                        onClick={() => setCurrentStep(1)}
                    >
                        Anterior
                    </button>
                    )}
                    {(currentStep === 1 || !config.ENABLE_PROPERTY_CARD_OCR) && (
                    <button
                        type="button"
                        className="btn-standard btn-standard-secondary"
                        onClick={onCancel}
                    >
                        Cancelar
                    </button>
                    )}

                    {enableFillInspectionData() && (
                        <button
                            type="button"
                            className="btn-standard btn-standard-primary"
                            onClick={fillInspectionData}
                        >
                            Llenar datos de prueba
                        </button>
                    )}

                    {currentStep === 1 ? (
                        <>
                            <button
                                type="button"
                                className="btn-standard btn-standard-secondary"
                                onClick={handleSkipPropertyCardRead}
                            >
                                Omitir OCR y continuar
                            </button>
                            <button
                                type="button"
                                className={`btn-standard btn-standard-primary ${!propertyCardFiles.propertyCardFront?.file || !propertyCardFiles.propertyCardBack?.file ? 'disabled' : ''}`}
                                onClick={handleReadPropertyCard}
                                disabled={!propertyCardFiles.propertyCardFront?.file || !propertyCardFiles.propertyCardBack?.file}
                            >
                                Leer documento
                            </button>
                        </>
                    ) : (
                    <button
                        type="button"
                        className={`btn-standard btn-standard-primary ${!isFormComplete() || isSubmitting ? 'disabled' : ''}`}
                        onClick={createInspection}
                        disabled={!isFormComplete() || isSubmitting}
                    >
                        {isSubmitting ? 'Creando Inspección...' : 'Crear Inspección'}
                    </button>
                    )}
                </div>
            </div>

            <ActionConfirmModal
                isOpen={showPlateConfirmModal}
                onClose={handleReviewPlate}
                onConfirm={handleConfirmPlate}
                title="Confirmar placa"
                confirmText="Continuar"
                cancelText="Revisar placa"
                message={
                    <p className="action-confirm-message">
                        Vas a registrar la placa{' '}
                        <span className="action-confirm-plate">{vehicleData.licensePlate}</span>.
                        {' '}¿Corresponde al vehículo en inspección?
                    </p>
                }
            />
            {/* Notification Component */}
            <Notification
                visible={notification.visible}
                message={notification.message}
                type={notification.type}
                onClose={() => setNotification({ visible: false, message: '', type: 'info' })}
            />
        </div>
    );
};

export default StandardInspectionForm;